import React from 'react';
import { FaUsers, FaVolumeMute, FaMicrophone, FaVideo, FaVideoSlash, FaDesktop } from 'react-icons/fa';
import { useParticipants, useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';

interface Participant {
    id: string;
    name: string;
    avatar?: string;
    isOnline?: boolean;
    isHost?: boolean;
    isMuted?: boolean;
    isVideoOn?: boolean;
    isScreenSharing?: boolean;
}

interface ParticipantsSidebarProps {
    participants: Participant[];
    hasToken?: boolean;
}

const ParticipantsSidebar: React.FC<ParticipantsSidebarProps> = ({ participants, hasToken }) => {
    const lkParticipants = useParticipants();
    const connectionState = useConnectionState();

    const connectionIndicator = hasToken ? (() => {
        if (connectionState === ConnectionState.Connected)
            return { color: 'bg-green-500', label: 'Connected' };
        if (connectionState === ConnectionState.Connecting || connectionState === ConnectionState.Reconnecting)
            return { color: 'bg-yellow-400', label: 'Connecting…' };
        return { color: 'bg-red-500', label: 'Disconnected' };
    })() : null;

    // When connected to LiveKit, use live data; fall back to Redux store otherwise
    const isLive = lkParticipants.length > 0;

    const displayParticipants = isLive
        ? lkParticipants.map((p): Participant => {
            const reduxParticipant = participants.find(rp => rp.id === p.identity || rp.name === p.name);
            return {
                id: p.identity,
                name: p.isLocal
                    ? `${p.name || reduxParticipant?.name || p.identity} (You)`
                    : (p.name || reduxParticipant?.name || p.identity),
                avatar: reduxParticipant?.avatar,
                isOnline: true,
                isMuted: !p.isMicrophoneEnabled,
                isVideoOn: p.isCameraEnabled,
                isScreenSharing: p.isScreenShareEnabled,
                isHost: reduxParticipant?.isHost || false,
            };
        })
        : participants;

    return (
        <div className="w-64 sm:w-80 bg-white border-r border-gray-200/60 flex flex-col h-full shadow-sm z-10 shrink-0">
            <div className="p-4 border-b border-gray-200/60 flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-2 text-gray-800 font-medium">
                    <FaUsers className="text-orange-600" />
                    <span>Participants ({displayParticipants.length})</span>
                </div>
                {connectionIndicator && (
                    <div className="flex items-center gap-1.5" title={connectionIndicator.label}>
                        <div className={`w-2.5 h-2.5 rounded-full ${connectionIndicator.color} ${connectionState === ConnectionState.Connecting || connectionState === ConnectionState.Reconnecting ? 'animate-pulse' : ''}`} />
                        <span className="text-xs text-gray-500">{connectionIndicator.label}</span>
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
                {displayParticipants.map((participant) => (
                    <div
                        key={participant.id}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-orange-50/50 transition-colors mb-2 border border-transparent hover:border-orange-100"
                    >
                        <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-lg">
                                {participant.avatar ? (
                                    <img src={participant.avatar} alt={participant.name} className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    participant.name.charAt(0).toUpperCase()
                                )}
                            </div>
                            <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${participant.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-gray-900 text-sm font-medium truncate">
                                    {participant.name}
                                </p>
                                {participant.isHost && (
                                    <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
                                        Host
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                            {participant.isScreenSharing && (
                                <FaDesktop className="text-blue-500" title="Screen sharing" />
                            )}
                            {participant.isVideoOn ? (
                                <FaVideo className="text-gray-400" title="Video on" />
                            ) : (
                                <FaVideoSlash className="text-red-500" title="Video off" />
                            )}
                            {participant.isMuted ? (
                                <FaVolumeMute className="text-red-500" title="Muted" />
                            ) : (
                                <FaMicrophone className="text-gray-400" title="Microphone on" />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ParticipantsSidebar;
